"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { ArrowLeft, Save, Loader2, HelpCircle, MessageSquare } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { Badge } from "../../ui/badge";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { SHAREPOINT_FY_YEAR } from "@/constants/sharepoint-config";

interface OpeningBalanceTestingSubstantiveProps {
  onBack?: () => void;
  procedureId?: string; // Optional: if provided, show only this procedure's questions
}

interface Question {
  id: string;
  question: string;
  guidance?: string; // Text in parentheses for tooltip
  requiresDocument?: boolean; // If Yes, Document
  documentLabel?: string; // Custom document label (e.g., "Document our procedures in 'Going Concern' section.")
  isInferenceQuestion?: boolean; // True for inference/conclusion question
}

interface Section {
  id: string;
  title: string;
  questions: Question[];
}

const SECTIONS: Section[] = [
  {
    id: "inquiry",
    title: "Inquiry with Predecessor Auditor",
    questions: [
      {
        id: "OBT-SUB-1",
        question: "Have we communicated in writing with the predecessor auditor in accordance with Clause (8) of Part I of First Schedule of the Chartered Accountants Act, 1949?",
      },
      {
        id: "OBT-SUB-2",
        question: "Are there any concerns about the professional competence and independence of the predecessor auditor?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-3",
        question: "Has the predecessor auditor raised any concerns/matters that would impact our engagement risk rating?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-4",
        question: "Does the predecessor auditor indicate that there are deficiencies in internal controls?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-5",
        question: "Are there any indications of current/past fraud?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-6",
        question: "Were there any identified litigations?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-7",
        question: "Are there any concerns about entity's ability to continue as Going Concern?",
        requiresDocument: true,
        documentLabel: "Document our procedures in 'Going Concern' section.",
      },
      {
        id: "OBT-SUB-8",
        question: "Were there any identified corrected/uncorrected misstatements?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-9",
        question: "Were any ABCOTDs not considered qualitatively and/or quantitatively material that should have been?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-10",
        question: "For each ABCOTD that was considered quantitatively and/or qualitatively material, is there any concern that the audit procedures performed were insufficient?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-11",
        question: "Were there any events after prior period-end that did not meet the subsequent event disclosure requirements that are of concern to our current-year engagement?",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-INFERENCE-INQUIRY",
        question: "Document the inference and conclusion from the above procedure performed.",
        requiresDocument: true,
        isInferenceQuestion: true,
      },
    ],
  },
  {
    id: "sa510",
    title: "SA 510 Assessment",
    questions: [
      {
        id: "OBT-SUB-12",
        question: "Have we obtained sufficient and appropriate audit evidence to conclude whether opening balances contain misstatement that may cause the current year financial statements to be materially misstated?",
        guidance: "(The procedures performed for obtaining sufficient and appropriate audit evidence include:\na) Where the prior year financial statements were audited, perusing the copies of the audited financial statements including the other relevant documents relating to the prior period financial statements;\nb) Evaluating whether audit procedures performed in the current period provide evidence relevant to the opening balances; or\nc) Performing specific audit procedures to obtain evidence regarding the opening balances.)",
      },
      {
        id: "OBT-SUB-13",
        question: "Where audit evidence is obtained that the opening balances contain misstatements that could materially affect the current period's financial statements, have we concluded that such misstatements exist in the current period's financial statements?",
        documentLabel: "Communicate the misstatements with the appropriate level of management and those charged with governance in accordance with SA 450.",
        requiresDocument: true,
      },
      {
        id: "OBT-SUB-14",
        question: "Have we obtained sufficient appropriate audit evidence about whether the accounting policies reflected in the opening balances have been consistently applied in the current period's financial statements, and whether changes in the accounting policies have been properly accounted for and adequately presented and disclosed in accordance with the applicable financial reporting framework?",
      },
      {
        id: "OBT-SUB-INFERENCE-SA510",
        question: "Document the inference and conclusion from the above procedure performed.",
        requiresDocument: true,
        isInferenceQuestion: true,
      },
    ],
  },
];

interface QuestionResponse {
  selectedOption?: string;
  textResponse?: { html: string; plainText: string } | null;
  documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
}

export default function OpeningBalanceTestingSubstantive({
  onBack,
  setActiveSection,
  procedureId,
}: OpeningBalanceTestingSubstantiveProps) {
  // If no onBack is provided but setActiveSection is, create a back handler
  const handleBack = onBack || (() => {
    if (setActiveSection) {
      setActiveSection("opnbal-substantive");
    }
  });
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("OBT"); // Default prefix
  const [questionResponses, setQuestionResponses] = useState<Record<string, QuestionResponse>>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `OPENING_BALANCE_TESTING_${normalizedSection}`;
  };

  const WORKSPACE_NAME = "Opening Balance Testing";

  // Filter sections based on procedureId
  const displaySections = procedureId 
    ? SECTIONS.filter(section => {
        if (procedureId === "inquiry-predecessor") return section.id === "inquiry";
        if (procedureId === "sa510-assessment") return section.id === "sa510";
        return false;
      })
    : SECTIONS;

  // Helper function to convert number to roman numeral
  const toRomanNumeral = (num: number): string => {
    const romanNumerals: [number, string][] = [
      [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'],
      [100, 'c'], [90, 'xc'], [50, 'l'], [40, 'xl'],
      [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i']
    ];
    
    let result = '';
    for (const [value, numeral] of romanNumerals) {
      while (num >= value) {
        result += numeral;
        num -= value;
      }
    }
    return result;
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load area prefix from Sections.json
  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            // Look for "Opening Balance Testing" in Execution section
            if (sectionsData.Execution && sectionsData.Execution[WORKSPACE_NAME]) {
              setAreaPrefix(sectionsData.Execution[WORKSPACE_NAME]);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to load area prefix from Sections.json, using default OBT:", error);
      }
    };
    loadAreaPrefix();
  }, []);

  // Load saved responses
  useEffect(() => {
    const loadSavedResponses = async () => {
      if (!companyName || !areaPrefix) {
        setLoading(false);
        return;
      }

      try {
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result?.data?.substantiveProcedures) {
          const savedData = result.data.substantiveProcedures;
          const loadedResponses: Record<string, QuestionResponse> = {};

          SECTIONS.forEach((section) => {
            section.questions.forEach((question) => {
              const savedQuestion = savedData[question.id];
              if (savedQuestion) {
                const docs = Array.isArray(savedQuestion.documents)
                  ? savedQuestion.documents.map((d: any) => {
                      if (typeof d === "string") {
                        return { id: `doc_${d}`, type: "linked", name: d };
                      }
                      return {
                        id: d.id || `doc_${d.name || d.fileName}`,
                        type: d.type || "linked",
                        name: d.name || d.fileName || "",
                        content: d.content,
                        url: d.url,
                      };
                    })
                  : [];

                loadedResponses[question.id] = {
                  selectedOption: savedQuestion.selectedOption || "",
                  textResponse: savedQuestion.textResponse || null,
                  documents: docs,
                };
              }
            });
          });

          setQuestionResponses(loadedResponses);
        }
      } catch (error) {
        console.error("Failed to load saved responses:", error);
      } finally {
        setLoading(false);
      }
    };

    if (companyName && areaPrefix) {
      loadSavedResponses();
    } else if (companyName) {
      setLoading(false);
    }
  }, [companyName, areaPrefix]);

  const updateSelectedOption = (questionId: string, value: string) => {
    setQuestionResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || { documents: [] }),
        selectedOption: value,
      },
    }));
  };

  const handleDocumentsChange = (questionId: string, documents: any[]) => {
    setQuestionResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || { selectedOption: "" }),
        documents: documents,
      },
    }));
  };

  const handleTextResponseChange = (questionId: string, textResponse: any) => {
    setQuestionResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || { selectedOption: "", documents: [] }),
        textResponse: textResponse,
      },
    }));
  };

  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      setSaveError("Missing required data (company name or area prefix)");
      setSaveStatus('error');
      return;
    }

    // Auto-save any pending textbox content before processing responses
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    // Wait for auto-save to complete
    await new Promise(resolve => setTimeout(resolve, 1500));

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;

      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save each question's response
      for (const section of SECTIONS) {
        for (const question of section.questions) {
          const response = questionResponses[question.id] || {};
          
          // For inference questions, query db.json for section field like CustomProcedure
          let documentNames: string[] = [];
          if (question.isInferenceQuestion) {
            // Query db.json to find all files with this section in their "section" field
            // Section format: Execution_<areaPrefix>_<questionId>
            const sectionToFind = `Execution_${areaPrefix}_${question.id}`;
            try {
              if ((window as any).sharePointAPI?.loadDocumentListBySection) {
                const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
                if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
                  documentNames = dbResult.data
                    .map((file: any) => file.name || '')
                    .filter((name: string) => name && name.trim().length > 0);
                }
              }
            } catch (error) {
              console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
            }
          } else {
            // For regular questions, use documents from state
            documentNames = (response.documents || []).map((d: any) => d.name || d);
          }

          // Check localStorage for textbox content (highest priority)
          const textboxFromLocalStorage = localStorage.getItem(`textbox_${question.id}`);
          const hasTextboxContent = textboxFromLocalStorage && textboxFromLocalStorage.trim().length > 0;
          
          let textResponse = response.textResponse || null;
          if (hasTextboxContent) {
            textResponse = {
              html: textboxFromLocalStorage,
              plainText: textboxFromLocalStorage.replace(/<[^>]*>/g, '')
            };
          }

          existingData.substantiveProcedures[question.id] = {
            selectedOption: response.selectedOption || "",
            textResponse: textResponse,
            documents: documentNames,
            table: null,
            lastUpdated: new Date().toISOString(),
            companyName,
          };
        }
      }

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: String(SHAREPOINT_FY_YEAR),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [companyName, areaPrefix, questionResponses]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            Opening Balance Testing - Substantive Procedures
          </h2>
          <p className="text-gray-400">
            Perform detailed procedures over Opening Balance Testing
          </p>
        </div>
        {(onBack || setActiveSection) && (
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        )}
      </div>

      {displaySections.map((section) => (
        <Card key={section.id} className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">{section.title}</CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId(section.title))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {section.questions.map((question, index) => {
              const response = questionResponses[question.id] || {};
              const showDocument = question.requiresDocument && (question.isInferenceQuestion || response.selectedOption === "Yes");

              return (
                <div key={question.id} className="space-y-4">
                  <div className="flex items-start gap-2">
                    <Label className="text-sm font-medium text-white flex-1">
                      <span className="mr-2">{toRomanNumeral(index + 1)}.</span>
                      {question.question.split('[')[0].trim()}
                      {question.guidance && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 ml-2 text-white/60 hover:text-white hover:bg-white/10"
                              >
                                <HelpCircle className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              className="max-w-md p-3 bg-gray-900 text-white border border-gray-700 whitespace-pre-line"
                            >
                              <p className="text-sm">{question.guidance}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </Label>
                  </div>

                  {!question.isInferenceQuestion && (
                    <RadioGroup
                      value={response.selectedOption || ""}
                      onValueChange={(value) => updateSelectedOption(question.id, value)}
                      className="ml-8"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="Yes" id={`${question.id}-yes`} className="border-white/20 text-[#4da3ff]" />
                        <Label htmlFor={`${question.id}-yes`} className="text-sm text-white/80 cursor-pointer">
                          Yes
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="No" id={`${question.id}-no`} className="border-white/20 text-[#4da3ff]" />
                        <Label htmlFor={`${question.id}-no`} className="text-sm text-white/80 cursor-pointer">
                          No
                        </Label>
                      </div>
                    </RadioGroup>
                  )}

                  {showDocument && (
                    <div className="ml-8 mt-4">
                      {question.documentLabel && (
                        <p className="text-xs text-white/80 mb-2 italic">{question.documentLabel}</p>
                      )}
                      <DocumentManager
                        sectionKey={`execution-substantive:${WORKSPACE_NAME}`}
                        itemId={question.id}
                        itemQuestion={question.question}
                        itemTextResponse={response.textResponse}
                        itemDocuments={response.documents || []}
                        onDocumentsChange={(docs) => handleDocumentsChange(question.id, docs)}
                        onTextResponseChange={(textResp) => handleTextResponseChange(question.id, textResp)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end items-center gap-4 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600">
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && saveError && (
          <Badge variant="destructive" className="text-red-600">
            Error: {saveError}
          </Badge>
        )}
        <Button
          onClick={handleSave}
          className="flex items-center gap-2"
          disabled={saving || !companyName}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Responses
            </>
          )}
        </Button>
      </div>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {};
            SECTIONS.forEach((section) => {
              sectionMap[getSectionQuestionId(section.title)] = section.title;
            });
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
